// Constants
import { CUSTOM_VIEW_SCHEMA_VERSION } from "@ms/uno-constants/lib/local/CustomViewsConstants";
import { RelativeDateToken } from "@ms/uno-constants/lib/local/DateConstants";
import { allFieldsToken, customFieldPrefix, ExpressionOperator } from "@ms/uno-constants/lib/local/ExpressionConstants";
import { MAX_SORT_LEVELS } from "@ms/uno-constants/lib/local/TaskGridConstants";
import { StatusFilterOption } from "../../../constants/FilterConstants";
// Errors
import { BaseError } from "@ms/uno-errors/lib/local/errors";
// Models
import { ViewType } from "@ms/uno-constants/lib/local/RoutingConstants";
import { PatchableAndDeltaSyncableODataEntity } from "../common/PatchableAndDeltaSyncableODataEntity";
import { PriorityName } from "../Enumerations";
import { customViewBuiltInFilterFieldNames, supportedTargetEntities } from "./ICrossViewCustomizations";
import { TargetView } from "./IPerViewCustomizations";
// Utilities
import { LocalIdGenerator } from "@ms/uno-utilities/lib/local/LocalIdGenerator";
import { applyDiffTemporalAndArrayCustomizer } from "@ms/uno-utilities/lib/local/ObjectUtilities";
import cloneDeep from "lodash/cloneDeep";
import mergeWith from "lodash/mergeWith";
import { Temporal } from "temporal-polyfill-lite";
import { ConvertPriorityNameToPriority } from "../../../utilities/ConversionUtilities";
import { createIdentitySet } from "../../../utilities/identity";
/** The closed set a `field` name is validated against. */ const customViewBuiltInFilterFields = new Set(customViewBuiltInFilterFieldNames);
/** The value kind each built-in field carries. */ const builtInFilterFieldValueKinds = {
    startDateTime: "dateTime",
    dueDateTime: "dateTime",
    finishDateTime: "dateTime",
    priority: "priority",
    status: "status",
    bucketId: "string",
    userAssignments: "string",
    goalIds: "string",
    appliedCategoryIds: "string"
};
/** The accepted values for the `status` field. */ const customViewStatusFilterValues = new Set(Object.values(StatusFilterOption));
/** The accepted values for the `priority` field. */ const customViewPriorityFilterValues = new Set(Object.values(PriorityName).map(ConvertPriorityNameToPriority));
/** The custom view entity implementation. */ export class CustomView extends PatchableAndDeltaSyncableODataEntity {
    mapEntityChangesToResource(changed, _source) {
        return {
            displayName: changed.displayName
        };
    }
    mapDeltaChangesToEntity(delta) {
        // displayName and schemaVersion are direct assignment.
        return this.processDeltaWithTranslators(delta, {
            crossViewCustomizations: (value)=>CustomView.parseCrossViewCustomizations(value),
            perViewCustomizations: (value)=>CustomView.parsePerViewCustomizations(value),
            initialView: (value)=>CustomView.parseInitialView(value),
            createdBy: (value)=>createIdentitySet(value),
            createdDateTime: (value)=>Temporal.Instant.from(value),
            lastModifiedBy: (value)=>createIdentitySet(value),
            lastModifiedDateTime: (value)=>Temporal.Instant.from(value),
            deletedBy: (value)=>value ? createIdentitySet(value) : null,
            deletedDateTime: (value)=>value ? Temporal.Instant.from(value) : null
        });
    }
    static fromResource(resource) {
        return new CustomView({
            id: resource.id,
            version: resource["@etag"],
            planId: resource.planId,
            displayName: resource.displayName,
            crossViewCustomizations: CustomView.parseCrossViewCustomizations(resource.crossViewCustomizations),
            perViewCustomizations: CustomView.parsePerViewCustomizations(resource.perViewCustomizations),
            initialView: CustomView.parseInitialView(resource.initialView),
            schemaVersion: resource.schemaVersion,
            createdBy: createIdentitySet(resource.createdBy),
            createdDateTime: Temporal.Instant.from(resource.createdDateTime),
            lastModifiedBy: createIdentitySet(resource.lastModifiedBy),
            lastModifiedDateTime: Temporal.Instant.from(resource.lastModifiedDateTime),
            deletedBy: resource.deletedBy ? createIdentitySet(resource.deletedBy) : null,
            deletedDateTime: resource.deletedDateTime ? Temporal.Instant.from(resource.deletedDateTime) : null
        });
    }
    /**
     * Build a brand-new custom view carrying the supplied cross-view and per-view customizations.
     * Owns the identity/version/schema wiring so callers only provide the captured view state plus
     * the author identity and timestamp, keeping this free of store and clock dependencies.
     */ static create(params) {
        const { planId, displayName, initialView, crossViewCustomizations, perViewCustomizations, createdBy, createdDateTime } = params;
        return new CustomView({
            id: LocalIdGenerator.generate(),
            version: "1",
            planId,
            displayName,
            initialView,
            schemaVersion: CUSTOM_VIEW_SCHEMA_VERSION,
            crossViewCustomizations,
            perViewCustomizations,
            createdBy,
            createdDateTime,
            lastModifiedBy: createdBy,
            lastModifiedDateTime: createdDateTime,
            deletedBy: null,
            deletedDateTime: null
        });
    }
    getClone() {
        return new CustomView({
            id: this.id,
            version: this.version,
            planId: this.planId,
            displayName: this.displayName,
            crossViewCustomizations: cloneDeep(this.crossViewCustomizations),
            perViewCustomizations: cloneDeep(this.perViewCustomizations),
            initialView: this.initialView,
            schemaVersion: this.schemaVersion,
            createdBy: cloneDeep(this.createdBy),
            createdDateTime: Temporal.Instant.from(this.createdDateTime),
            lastModifiedBy: cloneDeep(this.lastModifiedBy),
            lastModifiedDateTime: Temporal.Instant.from(this.lastModifiedDateTime),
            deletedBy: this.deletedBy ? cloneDeep(this.deletedBy) : null,
            deletedDateTime: this.deletedDateTime ? Temporal.Instant.from(this.deletedDateTime) : null
        });
    }
    setProperty(key, value) {
        const clone = this.getClone();
        clone[key] = value;
        return clone;
    }
    /**
     * A view's customizations arrive as whole documents, so a delta that removes a filter or a
     * column width signals it by omitting that field. A deep merge would instead fold the incoming
     * document into the stale one, keeping fields the delta meant to drop.
     * `crossViewCustomizations` and `perViewCustomizations` are replaced wholesale.
     */ applyDiffs(...diff) {
        if (diff.length === 0) {
            return this;
        }
        const customViewApplyDiffCustomizer = (source, target, key)=>key === "crossViewCustomizations" || key === "perViewCustomizations" ? target !== undefined ? cloneDeep(target) : undefined : applyDiffTemporalAndArrayCustomizer(source, target, key);
        return mergeWith(this.getClone(), ...diff, customViewApplyDiffCustomizer);
    }
    forSerialization() {
        return {
            id: this.id,
            "@etag": this.version,
            planId: this.planId,
            displayName: this.displayName,
            crossViewCustomizations: CustomView.serializeCrossViewCustomizations(this.crossViewCustomizations),
            perViewCustomizations: CustomView.serializePerViewCustomizations(this.perViewCustomizations),
            initialView: this.initialView,
            schemaVersion: this.schemaVersion,
            createdBy: this.createdBy,
            createdDateTime: this.createdDateTime.toString(),
            lastModifiedBy: this.lastModifiedBy,
            lastModifiedDateTime: this.lastModifiedDateTime.toString(),
            deletedBy: this.deletedBy,
            deletedDateTime: this.deletedDateTime?.toString() ?? null
        };
    }
    forCreate() {
        return {
            planId: this.planId,
            displayName: this.displayName,
            crossViewCustomizations: CustomView.serializeCrossViewCustomizations(this.crossViewCustomizations),
            perViewCustomizations: CustomView.serializePerViewCustomizations(this.perViewCustomizations),
            initialView: this.initialView,
            schemaVersion: this.schemaVersion
        };
    }
    /**
     * Resolves the view a custom view opens on. Board is the default if the value is not recognized.
     * @param value The wire value.
     * @returns The view type to open.
     */ static parseInitialView(value) {
        return Object.values(ViewType).find((viewType)=>viewType === value) ?? ViewType.Board;
    }
    //#region Cross-view customizations
    /**
     * Parses the cross-view customizations.
     * @param resource The wire container.
     * @returns The typed cross-view customizations.
     * @throws {BaseError} When a recognized entity's expression cannot be fully understood.
     */ static parseCrossViewCustomizations(resource) {
        const filters = resource?.filters;
        if (filters == null) {
            return {
                filters: {}
            };
        }
        const parsedFilters = {};
        for (const [entityKey, expression] of Object.entries(filters)){
            const targetEntity = CustomView.matchKey(supportedTargetEntities, entityKey);
            // Skip unrecognized entities.
            if (targetEntity === undefined) {
                continue;
            }
            // Keys are unique, so two of them collapsing to one entity is corrupt.
            if (parsedFilters[targetEntity] !== undefined) {
                throw CustomView.crossViewFilterError(targetEntity);
            }
            let expressionParsed;
            try {
                expressionParsed = JSON.parse(expression);
            } catch  {
                throw CustomView.crossViewFilterError(targetEntity);
            }
            parsedFilters[targetEntity] = CustomView.parseFilterNode(expressionParsed, targetEntity);
        }
        return {
            filters: parsedFilters
        };
    }
    /**
     * Serializes the cross-view customizations.
     * @param customizations The typed cross-view customizations.
     * @returns The wire container.
     */ static serializeCrossViewCustomizations(customizations) {
        const filters = {};
        for (const [targetEntity, expression] of Object.entries(customizations.filters)){
            if (expression === undefined) {
                continue;
            }
            filters[targetEntity] = JSON.stringify(expression);
        }
        return {
            filters
        };
    }
    /** Parses one node of an expression tree into a typed filter node, recursing into children. */ static parseFilterNode(expression, targetEntity) {
        if (!CustomView.isRecord(expression)) {
            throw CustomView.crossViewFilterError(targetEntity);
        }
        const operator = CustomView.matchOperator(expression.op);
        if (operator === undefined) {
            throw CustomView.crossViewFilterError(targetEntity);
        }
        // The operator both names what the node does and fixes which shape it has.
        switch(operator){
            case ExpressionOperator.And:
            case ExpressionOperator.Or:
                return CustomView.parseLogicalNode(expression, operator, targetEntity);
            case ExpressionOperator.Equals:
            case ExpressionOperator.Substring:
                return CustomView.parseComparisonNode(expression, operator, targetEntity);
            case ExpressionOperator.In:
                return CustomView.parseInNode(expression, targetEntity);
            /* istanbul ignore next -- exhaustiveness guard: the closed operator enum makes this unreachable. */ default:
                {
                    const unsupportedOperator = operator;
                    throw CustomView.crossViewFilterError(targetEntity, {
                        Operator: String(unsupportedOperator)
                    });
                }
        }
    }
    static parseComparisonNode(node, operator, targetEntity) {
        CustomView.rejectUnknownKeys(node, [
            "op",
            "field",
            "value"
        ], targetEntity);
        const field = CustomView.validateField(node.field, operator, targetEntity);
        const value = CustomView.parseScalar(field, node.value, targetEntity);
        return {
            op: operator,
            field,
            value
        };
    }
    static parseInNode(node, targetEntity) {
        CustomView.rejectUnknownKeys(node, [
            "op",
            "field",
            "value"
        ], targetEntity);
        const field = CustomView.validateField(node.field, ExpressionOperator.In, targetEntity);
        const value = node.value;
        if (!Array.isArray(value) || value.length === 0) {
            throw CustomView.crossViewFilterError(targetEntity);
        }
        const values = value.map((entry)=>CustomView.parseScalar(field, entry, targetEntity));
        // The empty case is already refused above, so the head is present; rebuilding the list as a
        // head-plus-rest tuple carries that fact into the node's non-empty type without a cast.
        const [firstValue, ...restValues] = values;
        return {
            op: ExpressionOperator.In,
            field,
            value: [
                firstValue,
                ...restValues
            ]
        };
    }
    static parseLogicalNode(node, operator, targetEntity) {
        CustomView.rejectUnknownKeys(node, [
            "op",
            "children"
        ], targetEntity);
        const children = node.children;
        // Logical nodes are n-ary with two or more children: a single condition is spelled as the
        // condition itself, so a one-child node means the payload was not written to this grammar.
        if (!Array.isArray(children) || children.length < 2) {
            throw CustomView.crossViewFilterError(targetEntity);
        }
        // Length is guaranteed above, so rebuilding as a two-plus-rest tuple carries the invariant
        // into the node's non-empty type without a cast.
        const [first, second, ...rest] = children.map((child)=>CustomView.parseFilterNode(child, targetEntity));
        return {
            op: operator,
            children: [
                first,
                second,
                ...rest
            ]
        };
    }
    /** Validates a node's field. */ static validateField(field, operator, targetEntity) {
        if (typeof field !== "string" || field.length === 0) {
            throw CustomView.crossViewFilterError(targetEntity);
        }
        // A keyword search is `substring` over every field, and neither half means anything alone: a
        // `substring` scoped to one field is a later extension, and `*` under any other operator has
        // no reading at all.
        const isAllFields = field === allFieldsToken;
        const isSubstring = operator === ExpressionOperator.Substring;
        if (isAllFields !== isSubstring) {
            throw CustomView.crossViewFilterError(targetEntity);
        }
        if (isAllFields) {
            return field;
        }
        // Custom Field task field filter
        if (field.startsWith(customFieldPrefix)) {
            const id = field.slice(customFieldPrefix.length);
            if (id.length === 0) {
                throw CustomView.crossViewFilterError(targetEntity);
            }
            return `${customFieldPrefix}${id}`;
        }
        // Built in fields
        if (!CustomView.isBuiltInFilterField(field)) {
            throw CustomView.crossViewFilterError(targetEntity);
        }
        return field;
    }
    /**
     * Narrows an untrusted value to a scalar and checks it against what its field can hold, returning
     * the validated value. A built-in field is checked against the kind it carries ({@link
     * builtInFilterFieldValueKinds}); `null` ("no value") is accepted for every field.
     */ static parseScalar(field, value, targetEntity) {
        if (!CustomView.isScalar(value)) {
            throw CustomView.crossViewFilterError(targetEntity);
        }
        if (field === allFieldsToken) {
            // A keyword search has nothing to match but a non-empty string.
            if (typeof value !== "string" || value.length === 0) {
                throw CustomView.crossViewFilterError(targetEntity);
            }
            return value;
        }
        // `null` is the uniform "no value" comparand every field accepts.
        if (value === null) {
            return value;
        }
        // A custom field ref accepts any scalar here: its value type lives with the plan's field
        // definitions, which this layer doesn't have, so there's nothing to check against.
        if (!CustomView.isBuiltInFilterField(field)) {
            return value;
        }
        const kind = builtInFilterFieldValueKinds[field];
        switch(kind){
            case "priority":
                if (typeof value !== "number" || !customViewPriorityFilterValues.has(value)) {
                    throw CustomView.crossViewFilterError(targetEntity);
                }
                return value;
            case "string":
                if (typeof value !== "string") {
                    throw CustomView.crossViewFilterError(targetEntity);
                }
                return value;
            case "status":
                if (typeof value !== "string" || !customViewStatusFilterValues.has(value)) {
                    throw CustomView.crossViewFilterError(targetEntity);
                }
                return value;
            case "dateTime":
                {
                    if (typeof value !== "string") {
                        throw CustomView.crossViewFilterError(targetEntity);
                    }
                    const isToken = Object.values(RelativeDateToken).some((token)=>token === value);
                    if (!isToken && !CustomView.isAbsoluteDate(value)) {
                        throw CustomView.crossViewFilterError(targetEntity);
                    }
                    return value;
                }
            /* istanbul ignore next -- exhaustiveness guard: the field->kind map only yields kinds the cases above handle. */ default:
                throw CustomView.crossViewFilterError(targetEntity, {
                    ValueKind: String(kind)
                });
        }
    }
    /** True when the string names an absolute date or date-time. */ static isAbsoluteDate(value) {
        try {
            Temporal.PlainDate.from(value);
            return true;
        } catch  {
        // Not a plain calendar date; it may still carry a time.
        }
        /* istanbul ignore next -- unreachable: PlainDate.from already accepts any zoneless date-time by truncating to its date, so this branch only stands as defense in depth. */ try {
            Temporal.PlainDateTime.from(value);
            return true;
        } catch  {
        // Not a zoneless date-time; it may still carry an offset or zone.
        }
        try {
            Temporal.Instant.from(value);
            return true;
        } catch  {
            return false;
        }
    }
    /** Rejects any property outside the grammar. */ static rejectUnknownKeys(node, allowed, targetEntity) {
        for (const key of Object.keys(node)){
            if (!allowed.includes(key)) {
                throw CustomView.crossViewFilterError(targetEntity);
            }
        }
    }
    /** Return error to be thrown */ static crossViewFilterError(targetEntity, details) {
        /* istanbul ignore next -- details are only supplied by the exhaustiveness-guard defaults, which are themselves unreachable; without them the extra string is empty. */ const extra = Object.entries(details ?? {}).map(([name, value])=>`[${name}=${value}]`).join("");
        return new BaseError(`Deserialization Error: [Entity=CustomView][Field=crossViewCustomizations.filters][Key=${targetEntity}]${extra}`, false);
    }
    /** Matches a modeled key case-insensitively, since keys are unique that way. Undefined when unknown. */ static matchKey(known, raw) {
        /* istanbul ignore next -- unreachable: the only caller passes object keys from Object.entries, which are always strings. */ if (typeof raw !== "string") {
            return undefined;
        }
        return known.find((candidate)=>candidate.toLowerCase() === raw.toLowerCase());
    }
    /** Operators are matched exactly (unlike modeled keys), since they are payload rather than envelope. */ static matchOperator(raw) {
        return typeof raw === "string" ? Object.values(ExpressionOperator).find((candidate)=>candidate === raw) : undefined;
    }
    static isScalar(value) {
        return value === null || typeof value === "string" || typeof value === "number" || typeof value === "boolean";
    }
    /** Narrows an untrusted name to a built-in field. */ static isBuiltInFilterField(field) {
        return customViewBuiltInFilterFields.has(field);
    }
    //#endregion Cross-view customizations
    //#region Per-view customizations
    /**
     * Parses the per-view customizations from their wire resource.
     * Unknown attributes inside a customizations document are ignored.
     *
     * @param resource The wire entries, keyed by view type.
     * @returns The typed per-view customizations, one per view type.
     * @throws {BaseError} When a view type's customizations cannot be understood, or are missing.
     */ static parsePerViewCustomizations(resource) {
        const calendarCustomization = resource[TargetView.TaskCalendar] !== undefined ? CustomView.parseCalendarCustomizations(CustomView.getViewCustomizations(resource, TargetView.TaskCalendar)) : undefined;
        // Assembled a key at a time, since each view type has its own parser and its own shape.
        // Calendar is only keyed when present, so an absent calendar stays absent rather than
        // becoming an own property set to `undefined`.
        return {
            [TargetView.TaskGrid]: CustomView.parseGridCustomizations(CustomView.getViewCustomizations(resource, TargetView.TaskGrid)),
            [TargetView.TaskBoard]: CustomView.parseBoardCustomizations(CustomView.getViewCustomizations(resource, TargetView.TaskBoard)),
            [TargetView.TaskTimeline]: CustomView.parseTimelineCustomizations(CustomView.getViewCustomizations(resource, TargetView.TaskTimeline)),
            ...calendarCustomization !== undefined ? {
                [TargetView.TaskCalendar]: calendarCustomization
            } : {}
        };
    }
    /**
     * Serializes the per-view customizations to their wire resource, one entry per view type.
     * @param customizations The typed per-view customizations.
     * @returns The wire entries, keyed by view type.
     */ static serializePerViewCustomizations(customizations) {
        const resource = {};
        for (const targetView of Object.values(TargetView)){
            const viewCustomizations = customizations[targetView];
            if (viewCustomizations !== undefined) {
                resource[targetView] = JSON.stringify(viewCustomizations);
            }
        }
        return resource;
    }
    static perViewCustomizationsError(targetView) {
        return new BaseError(`Deserialization Error: [Entity=CustomView][Field=perViewCustomizations][Key=${targetView}]`, false);
    }
    static isRecord(value) {
        return typeof value === "object" && value !== null && !Array.isArray(value);
    }
    /**
     * Gets customizations for a specific view type.
     * @param resource The wire entries, keyed by view type.
     * @param targetView The view type to find.
     * @returns The view customizations.
     * @throws {BaseError} When the view type is missing or the customization cannot be understood.
     */ static getViewCustomizations(resource, targetView) {
        const raw = resource[targetView];
        if (raw === undefined) {
            throw CustomView.perViewCustomizationsError(targetView);
        }
        let parsed;
        try {
            parsed = JSON.parse(raw);
        } catch  {
            throw CustomView.perViewCustomizationsError(targetView);
        }
        if (!CustomView.isRecord(parsed)) {
            throw CustomView.perViewCustomizationsError(targetView);
        }
        return parsed;
    }
    /**
     * Parses a list of strings, throwing when the value is not an array of strings.
     * @param value The raw value to parse.
     * @param targetView The view type being parsed.
     * @returns The string list, empty when the view saves no list.
     * @throws {BaseError} When the value is not an array of strings.
     */ static parseStringList(value, targetView) {
        if (value === undefined) {
            return [];
        }
        if (!Array.isArray(value)) {
            throw CustomView.perViewCustomizationsError(targetView);
        }
        return value.map((entry)=>{
            if (typeof entry !== "string") {
                throw CustomView.perViewCustomizationsError(targetView);
            }
            return entry;
        });
    }
    /**
     * Parses the grid's sort, an ordered list with the highest priority level first.
     * @param value The raw sort attribute.
     * @param targetView The view type being parsed.
     * @returns The sort levels, empty when the view saves no sort.
     * @throws {BaseError} When the sort cannot be understood or carries more levels than supported.
     */ static parseSortKeys(value, targetView) {
        if (value === undefined) {
            return [];
        }
        if (!Array.isArray(value) || value.length > MAX_SORT_LEVELS) {
            throw CustomView.perViewCustomizationsError(targetView);
        }
        return value.map((entry)=>{
            if (!CustomView.isRecord(entry) || typeof entry.field !== "string" || entry.field.length === 0 || typeof entry.isAscending !== "boolean") {
                throw CustomView.perViewCustomizationsError(targetView);
            }
            return {
                field: entry.field,
                isAscending: entry.isAscending
            };
        });
    }
    static parseColumnWidths(value, targetView) {
        if (value === undefined) {
            return {};
        }
        if (!CustomView.isRecord(value)) {
            throw CustomView.perViewCustomizationsError(targetView);
        }
        const widths = {};
        for (const columnId of Object.keys(value)){
            const width = value[columnId];
            // A width is a pixel measurement, so zero or below names no column the grid could draw.
            if (typeof width !== "number" || !Number.isFinite(width) || width <= 0) {
                throw CustomView.perViewCustomizationsError(targetView);
            }
            widths[columnId] = width;
        }
        return widths;
    }
    static parseGridCustomizations(customizations) {
        const targetView = TargetView.TaskGrid;
        return {
            sort: CustomView.parseSortKeys(customizations.sort, targetView),
            columns: CustomView.parseStringList(customizations.columns, targetView),
            columnWidths: CustomView.parseColumnWidths(customizations.columnWidths, targetView)
        };
    }
    static parseBoardCustomizations(customizations) {
        const groupBy = customizations.groupBy;
        if (typeof groupBy !== "string" || groupBy.length === 0) {
            throw CustomView.perViewCustomizationsError(TargetView.TaskBoard);
        }
        return {
            groupBy
        };
    }
    static parseTimelineCustomizations(customizations) {
        const zoom = customizations.zoom;
        if (typeof zoom !== "number" || !Number.isInteger(zoom) || zoom <= 0) {
            throw CustomView.perViewCustomizationsError(TargetView.TaskTimeline);
        }
        return {
            zoom
        };
    }
    static parseCalendarCustomizations(customizations) {
        const viewMode = customizations.viewMode;
        if (typeof viewMode !== "string" || viewMode.length === 0) {
            throw CustomView.perViewCustomizationsError(TargetView.TaskCalendar);
        }
        return {
            viewMode
        };
    }
    constructor(propertyBag){
        super(propertyBag);
        this.planId = propertyBag.planId;
        this.displayName = propertyBag.displayName;
        this.crossViewCustomizations = cloneDeep(propertyBag.crossViewCustomizations);
        this.perViewCustomizations = cloneDeep(propertyBag.perViewCustomizations);
        this.initialView = propertyBag.initialView;
        this.schemaVersion = propertyBag.schemaVersion;
        this.createdBy = cloneDeep(propertyBag.createdBy);
        this.createdDateTime = propertyBag.createdDateTime;
        this.lastModifiedBy = cloneDeep(propertyBag.lastModifiedBy);
        this.lastModifiedDateTime = propertyBag.lastModifiedDateTime;
        this.deletedBy = cloneDeep(propertyBag.deletedBy);
        this.deletedDateTime = propertyBag.deletedDateTime;
    }
}
